import React from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import type { RootState } from "@store/store";
import CustomizeChallanModal from "@components/Challan/CustomizeChallanModal";
import InstituteDetailsModal from "@components/InstituteDetails/InstituteDetailsModal";
import AddEquipmentModal from "@features/CourseBuilder/components/EquipmentsTab/components/AddEquipmentModal";
import CreateLabModal from "@features/CourseBuilder/components/LabsTab/components/CreateLabModal/CreateLabModal";
import { CommitteeDetailsModal } from "@features/Committees/components";
import ProfileModal from "@features/Profile/components/ProfileModal";
import SignatureRequiredModal from "@features/Profile/components/SignatureRequiredModal";
import CommonModal from "@components/common/Modal/Modal";
import { closeModal, closeEditEquipmentModal, closeEditLabModal, openModal } from "@store/slices/modalSlice";
import { MODAL_KEYS } from "@constants/global.constants";

const ModalManager: React.FC = () => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const modalState = useSelector((state: RootState) => state.modal);

	const closeProfileModal = () => dispatch(closeModal(MODAL_KEYS.PROFILE));
	const openProfileFromSignaturePrompt = () => {
		dispatch(closeModal(MODAL_KEYS.SIGNATURE_REQUIRED));
		dispatch(openModal(MODAL_KEYS.PROFILE));
	};

	return (
		<>
			{/* Customize Challan Modal */}
			<CustomizeChallanModal
				isOpen={modalState.customizeChallan.isOpen}
				onClose={() => dispatch(closeModal(MODAL_KEYS.CHALLAN_CUSTOMIZE))}
			/>

			{/* Institute Details Modal */}
			<InstituteDetailsModal
				open={modalState.instituteDetailsModal.isOpen}
				onClose={() => dispatch(closeModal(MODAL_KEYS.INSTITUTE_DETAILS))}
			/>

			{/* Equipment Modal - handles both add and edit */}
			<AddEquipmentModal
				open={modalState.addEquipmentModal.isOpen || modalState.editEquipmentModal.isOpen}
				onClose={() => {
					if (modalState.addEquipmentModal.isOpen) {
						dispatch(closeModal(MODAL_KEYS.ADD_EQUIPMENT));
					} else {
						dispatch(closeEditEquipmentModal());
					}
				}}
				initialValues={modalState.editEquipmentModal.equipment}
				isEdit={modalState.editEquipmentModal.isOpen}
			/>

			{/* Lab Modal - handles both add and edit */}
			<CreateLabModal
				open={modalState.addLabModal.isOpen || modalState.editLabModal.isOpen}
				onClose={() => {
					if (modalState.addLabModal.isOpen) {
						dispatch(closeModal(MODAL_KEYS.ADD_LAB));
					} else {
						dispatch(closeEditLabModal());
					}
				}}
				initialValues={modalState.editLabModal.lab}
				isEdit={modalState.editLabModal.isOpen}
			/>

			{/* Committee Details Modal */}
			<CommitteeDetailsModal
				isOpen={modalState.committeeDetailsModal.isOpen}
				onClose={() => dispatch(closeModal(MODAL_KEYS.COMMITTEE_DETAILS))}
			/>

			{/* Signature prompt first in DOM so profile modal stacks above when both are momentarily open */}
			<SignatureRequiredModal
				isOpen={modalState.signatureRequiredModal.isOpen}
				onOpenProfile={openProfileFromSignaturePrompt}
			/>

			<CommonModal
				isOpen={modalState.profileModal.isOpen}
				onClose={closeProfileModal}
				title={t("psdaCommon.profile.profileTitle")}
				showCloseIcon
				width="max-w-xl"
				height="max-h-[95vh]"
				rounded="rounded-2xl"
				padding="p-6">
				<ProfileModal HideProfile={closeProfileModal} />
			</CommonModal>
		</>
	);
};

export default ModalManager;
